import type { CenterProps, MotionPropsFor, StackProps } from '@hover/blueprint';
import {
  Icon as BlueprintIcon,
  Body,
  Box,
  Button,
  Center,
  HStack,
  Image,
  VStack,
  motion,
  useBreakpointValue,
} from '@hover/blueprint';
import type { IconProp, PictogramProp } from '@hover/icons';
import { iconEdit, iconInspiration, iconUpload } from '@hover/icons';
import { curry } from 'ramda';
import type { Dispatch, SetStateAction } from 'react';
import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';

import {
  categoryToRouteParams,
  useCustomizeMenuForImage,
} from 'src/modules/homeownerApp/components/Customize/menu';
import { isCobrandCategory } from 'src/modules/homeownerApp/components/Customize/partners';
import { TileButtonPictogram } from 'src/modules/homeownerApp/components/TileButton';
import type {
  CustomizationCategory,
  MaterialRegionOrPaint,
} from 'src/modules/homeownerApp/constants/customizationCategories';
import {
  getGeneratedImageLabel,
  useHomeownerAppContext,
} from 'src/modules/homeownerApp/hooks';
import {
  usePartner,
  usePartnerCobranding,
} from 'src/modules/homeownerApp/hooks/usePartner';
import { BeforeAfterSwitch } from 'src/modules/pro/components/ImageGrid/ImageGrid';
import { Skeleton } from 'src/shared/components/Skeleton/Skeleton';
import type { AiDisplayImageFragment } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useQueryParam,
  useTranslate,
} from 'src/shared/hooks';
import { Route, generatePath } from 'src/shared/route';
import { buttonPressed } from 'src/shared/telemetry';
import { fadePresence } from 'src/shared/utils/motion';
import { hideScrollbars } from 'src/shared/utils/styles';

import { CarouselBadge } from '../../CarouselBadge';
import { CobrandLogo } from '../../CobrandLogo/CobrandLogo';
import { PeekingCarousel } from '../../PeekingCarousel';

interface LandingPageImageProps extends CenterProps {
  image: AiDisplayImageFragment;
  isBeforeSelected: boolean;
  isLoaded: boolean;
  isBefore?: boolean;
  isDesktop: boolean;
  setIsBeforeSelected: Dispatch<SetStateAction<boolean>>;
  setIsLoaded: Dispatch<SetStateAction<boolean>>;
}

type RouteParams = { [key: string]: string | number | boolean | undefined };

const createCustomizePathFn = curry(
  (path: string, params: RouteParams, categoryParams: RouteParams) =>
    generatePath(path, { ...params, ...categoryParams }),
);

const LandingPageImage = ({
  image,
  isBeforeSelected,
  setIsBeforeSelected,
  setIsLoaded,
  isLoaded,
  isDesktop,
  isBefore = false,
  children,
  ...props
}: LandingPageImageProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const isSelected = isBefore ? isBeforeSelected : !isBeforeSelected;
  const animate = isDesktop || isSelected ? 'visible' : 'hidden';
  const isCarouselEnabled = useQueryParam('showCarousel') === 'true';

  const { trackingData } = useHomeownerAppContext();
  const beforeAfterTrackingData = {
    ...trackingData,
    track_id: 'design-original-toggle',
  };
  const cobranding = usePartnerCobranding();

  // Temporary duplication during intermediate state where both carousel
  // and non-carousel versions are available.
  if (isCarouselEnabled) {
    return (
      <motion.div
        animate={animate}
        initial={animate}
        key={image.id}
        position={
          isBefore && !isDesktop
            ? 'relative'
            : { base: 'absolute', tablet: 'relative' }
        }
        variants={{
          hidden: { opacity: 0 },
          visible: { opacity: 1 },
        }}
      >
        <Skeleton height="100%" isLoaded={isLoaded}>
          <Center flexDir="column" height="100%" position="relative" {...props}>
            <Image
              alt={`embed-landing-page-image-${image.id}`}
              borderRadius={400}
              draggable={false}
              h="calc(100dvw - 80px)"
              maxH={{
                base: cobranding ? '255px' : '280px',
                tablet: '496px',
              }}
              maxW={{ base: '245px', tablet: '496px' }} // For now, always enforce square images
              objectFit="cover"
              onLoad={() => setIsLoaded(true)}
              src={image?.aiGeneratedImage?.generatedImagePath}
              w={{ base: 'calc(100dvw - 80px)', tablet: '100%' }}
            />
            <Box
              display={{ base: 'none', tablet: 'flex' }}
              left={500}
              position="absolute"
              top={500}
            >
              <CarouselBadge
                label={getGeneratedImageLabel(image?.aiGeneratedImage, t)}
              />
            </Box>
            <Box
              bottom={400}
              display={{ base: 'flex', tablet: 'none' }}
              position="absolute"
            >
              <BeforeAfterSwitch
                afterText="design"
                beforeText="original"
                isBeforeSelected={isBeforeSelected}
                setIsBeforeSelected={setIsBeforeSelected}
                trackingData={beforeAfterTrackingData}
              />
            </Box>
            {children}
          </Center>
        </Skeleton>
      </motion.div>
    );
  }
  return (
    <motion.div
      animate={animate}
      initial={animate}
      key={image.id}
      position={{ base: 'absolute', tablet: 'relative' }}
      px={{ base: 500, tablet: 0 }}
      variants={{
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
      }}
    >
      <Skeleton height="100%" isLoaded={isLoaded}>
        <Center flexDir="column" height="100%" position="relative" {...props}>
          <Image
            alt={`embed-landing-page-image-${image.id}`}
            borderRadius={400}
            draggable={false}
            maxH={{ base: '245px', tablet: '496px' }}
            maxW="100%"
            objectFit="cover"
            onLoad={() => setIsLoaded(true)}
            src={image?.aiGeneratedImage?.generatedImagePath}
          />
          <Box
            display={{ base: 'none', tablet: 'flex' }}
            left={500}
            position="absolute"
            top={500}
          >
            <CarouselBadge
              label={getGeneratedImageLabel(image?.aiGeneratedImage, t)}
            />
          </Box>
          <Box
            alignItems="center"
            bottom={0}
            display={{ base: 'flex', tablet: 'none' }}
            justifyContent="center"
            pb={700}
            position="relative"
            pt={500}
          >
            <Box bottom={950} position="absolute">
              <BeforeAfterSwitch
                afterText="design"
                beforeText="original"
                isBeforeSelected={isBeforeSelected}
                setIsBeforeSelected={setIsBeforeSelected}
                trackingData={beforeAfterTrackingData}
              />
            </Box>
          </Box>
          {children}
        </Center>
      </Skeleton>
    </motion.div>
  );
};

export const LandingPagePictogramButton = ({
  item,
  isLoading,
  toPath,
}: {
  item: EmbedMenuItem;
  isLoading: boolean;
  toPath: { pathname: string };
}) => {
  const { trackingData: contextTrackingData } = useHomeownerAppContext();
  const cobranding = usePartnerCobranding();
  return (
    <VStack data-testid="customize-menu-item" key={item.label}>
      <TileButtonPictogram
        as={Link}
        colorScheme="dark"
        disabled={isLoading}
        icon={item.icon}
        isLoading={isLoading}
        label={item.label}
        onClick={() => {
          buttonPressed({
            ...contextTrackingData,
            button_text: item.label,
            primary_cta: false,
            track_id: 'edit-category',
          });
        }}
        pictogram={item.pictogram}
        textDecoration="none"
        textSize={200}
        to={toPath}
        width={{ base: '72px', tablet: '90px' }}
      />
      {cobranding &&
        item.category &&
        isCobrandCategory(cobranding.type, item.category) && (
          <CobrandLogo colorMode="dark" hideHoverMark />
        )}
    </VStack>
  );
};

interface EmbedCustomizeMenuProps extends MotionPropsFor<StackProps> {
  menu: EmbedMenuItem[];
  isLoading: boolean;
  targetImage: AiDisplayImageFragment;
}

export interface EmbedMenuItem {
  category?: null | CustomizationCategory;
  label: string;
  icon: IconProp;
  pictogram?: PictogramProp;
  disabled?: boolean;
}

const MotionHStack = motion(hideScrollbars(HStack));

const EmbedCustomizeMenu = ({
  isLoading,
  menu: menuProp,
  targetImage: image,
  ...props
}: EmbedCustomizeMenuProps) => {
  const menu = isLoading ? menuProp.slice(0, 2) : menuProp;
  const isCarouselEnabled = useQueryParam('showCarousel') === 'true';

  return (
    <Box justifyContent="center" width="100%">
      <MotionHStack
        alignItems="flex-start"
        data-testid="customize-menu"
        display={{ base: 'none', tablet: 'flex' }}
        flexDirection="row"
        justifyContent="start"
        layout
        overflowX="auto"
        px={isCarouselEnabled ? undefined : { base: 400, tablet: 0 }}
        py="500"
        spacing="200"
        whiteSpace="nowrap"
        width="fit-content"
        {...fadePresence}
        {...props}
      >
        <LandingPagePictogramButton
          isLoading={isLoading}
          item={{
            label: 'Ideas',
            icon: iconInspiration,
          }}
          key="Ideas"
          toPath={{
            pathname: generatePath(Route.HOMEOWNER_APP_CUSTOMIZE_INSPIRE.path, {
              aiDisplayImageId: image.id,
            }),
          }}
        />
        {menu.map(item => (
          <LandingPagePictogramButton
            isLoading={isLoading}
            item={item}
            key={item.label}
            toPath={{
              pathname: generatePath(Route.HOMEOWNER_APP_CUSTOMIZE.path, {
                aiDisplayImageId: image.id,
                ...(item.category
                  ? categoryToRouteParams(item.category)
                  : undefined),
              }),
            }}
          />
        ))}
      </MotionHStack>
    </Box>
  );
};

export const EmbedLandingPage = ({
  originalImage,
  staticDesignedImage,
  featuredDisplayImages,
  onLeadSheetToggle,
}: {
  originalImage?: AiDisplayImageFragment;
  staticDesignedImage?: AiDisplayImageFragment;
  featuredDisplayImages: AiDisplayImageFragment[];
  onLeadSheetToggle: () => void;
}) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const isCarouselEnabled = useQueryParam('showCarousel') === 'true';
  // Temporary toggle between using the design item passed in, or from the array if carousel is enabled.
  const designedImage = isCarouselEnabled
    ? featuredDisplayImages[0]
    : staticDesignedImage;
  const [isBeforeSelected, setIsBeforeSelected] = useState(false);
  const isDesktop = useBreakpointValue({ base: false, tablet: true });
  const [isBeforeLoaded, setIsBeforeLoaded] = useState(false);
  const [isAfterLoaded, setIsAfterLoaded] = useState(false);
  const targetImage = isBeforeSelected ? originalImage : designedImage;
  const cobranding = usePartnerCobranding();
  const { isLoading } = usePartner();
  const history = useHistory();
  const allImagesLoaded = isBeforeLoaded && isAfterLoaded && !isLoading;

  const { menu, loading: menuLoading } = useCustomizeMenuForImage(
    t,
    designedImage,
  );

  const { trackingData } = useHomeownerAppContext();

  if (!originalImage || !designedImage || !targetImage) return null;

  const onUploadCarouselElementClick = () => {
    buttonPressed({
      ...trackingData,
      button_text: 'Upload a photo of your home',
      button_location: 'embed-photo-carousel',
      track_id: 'upload-photo-carousel',
    });

    onLeadSheetToggle();
  };

  const getEditPath = (
    aiDisplayImageId: number,
    categoryName?: MaterialRegionOrPaint,
  ) => {
    const generateCustomizePath = createCustomizePathFn(
      Route.HOMEOWNER_APP_CUSTOMIZE.path,
      { aiDisplayImageId },
    );

    if (categoryName === 'paint') {
      return generateCustomizePath(
        categoryToRouteParams({
          type: categoryName,
          region: 'walls',
        }),
      );
    }
    if (categoryName === 'windows') {
      return generateCustomizePath(
        categoryToRouteParams({
          type: 'material',
          region: 'windows',
        }),
      );
    }
    return generateCustomizePath({ category: 'inspire' });
  };

  const handleEditButtonClick = () => {
    buttonPressed({
      ...trackingData,
      button_text: 'Edit',
      track_id: 'sample-edit-photo-carousel',
    });
    history.push({ pathname: getEditPath(designedImage.id, cobranding?.type) });
  };

  const handleMoreIdeasButtonClick = () => {
    buttonPressed({
      ...trackingData,
      button_text: 'More ideas',
      track_id: 'sample-more-ideas-photo-carousel',
    });
    history.push({
      pathname: generatePath(Route.HOMEOWNER_APP_CUSTOMIZE_INSPIRE.path, {
        aiDisplayImageId: designedImage.id,
      }),
    });
  };

  const editButtonText = cobranding?.type
    ? `${t('projects.edit.edit')} ${cobranding?.type}`
    : t('projects.edit.edit');

  const updateRoute = (index: number) => {
    const currentDisplayImage = featuredDisplayImages?.[index];

    if (currentDisplayImage) {
      buttonPressed({
        ...trackingData,
        button_text: currentDisplayImage.style || 'Unknown',
        style: currentDisplayImage.style || undefined,
        space_type: currentDisplayImage.spaceType || undefined,
        track_id: 'sample-design-style-ideas',
      });
    }
  };

  const carouselElements: React.ReactElement[] = [];

  featuredDisplayImages.forEach(aiDisplayImage => {
    carouselElements.push(
      <HStack
        justifyContent="center"
        // Most constraints for the carousel element size are enforced on the image itself.
        // This is the only constraint for the upper level container.
        maxW={{
          base: 'auto',
          tablet: 'calc(100dvw - 120px)',
        }}
        pb={{ base: 0, tablet: 200 }}
        position="relative"
      >
        <LandingPageImage
          image={originalImage}
          isBefore
          isBeforeSelected={isBeforeSelected}
          isDesktop={isDesktop}
          isLoaded={allImagesLoaded}
          setIsBeforeSelected={setIsBeforeSelected}
          setIsLoaded={setIsBeforeLoaded}
        />
        <LandingPageImage
          image={aiDisplayImage}
          isBeforeSelected={isBeforeSelected}
          isDesktop={isDesktop}
          isLoaded={allImagesLoaded}
          setIsBeforeSelected={setIsBeforeSelected}
          setIsLoaded={setIsAfterLoaded}
        >
          <Box
            bottom={0}
            display={{ base: 'none', tablet: 'flex' }}
            gap="200"
            justifyContent="center"
            pb={400}
            position="absolute"
          >
            <Button
              color="light"
              iconAfter={iconEdit}
              label={t('embed.edit')}
              onClick={handleEditButtonClick}
              variant="glass"
            >
              {editButtonText}
            </Button>
            <Button
              color="light"
              iconAfter={iconInspiration}
              label={t('embed.moreIdeas')}
              onClick={handleMoreIdeasButtonClick}
              variant="glass"
            >
              {t('embed.moreIdeas')}
            </Button>
          </Box>
        </LandingPageImage>
      </HStack>,
    );
    carouselElements.push(
      <Button
        border="1px solid rgba(232, 232, 232, 0.48)"
        borderRadius="large"
        height={1}
        justifyContent="center"
        maxW={{ base: '245px', tablet: '496px' }}
        onClick={onUploadCarouselElementClick}
        padding={0}
        pb={{ base: 0, tablet: 200 }}
        position="relative"
        w="calc(100dvw - 80px)"
      >
        <Box alignItems="center" flexDir="column">
          <BlueprintIcon color="white" icon={iconUpload} />
          <Body
            color="white"
            fontWeight={700}
            size="small"
            sx={{ 'text-wrap': 'wrap' }}
            textAlign="center"
            width="70%"
          >
            {t('embed.uploadAPhoto')}
          </Body>
        </Box>
      </Button>,
    );
  });

  if (isCarouselEnabled) {
    return (
      <Box flexDirection="column" h={1} justifyContent="space-between">
        <PeekingCarousel setCurrentIndex={updateRoute}>
          {carouselElements}
        </PeekingCarousel>
        {menu && (
          <EmbedCustomizeMenu
            display={{ base: 'flex', tablet: 'none' }}
            isLoading={menuLoading}
            menu={menu}
            targetImage={targetImage}
            zIndex={1}
          />
        )}
      </Box>
    );
  }
  return (
    <Box flexDirection="column" h={1} justifyContent="space-between">
      <HStack
        gap="500"
        h={1}
        justifyContent="center"
        pb={{ base: 0, tablet: 200 }}
        position="relative"
        px={500}
        top={{ base: 700, tablet: 0 }}
      >
        <LandingPageImage
          image={originalImage}
          isBefore
          isBeforeSelected={isBeforeSelected}
          isDesktop={isDesktop}
          isLoaded={isBeforeLoaded && isAfterLoaded && !isLoading}
          setIsBeforeSelected={setIsBeforeSelected}
          setIsLoaded={setIsBeforeLoaded}
        />
        <LandingPageImage
          image={designedImage}
          isBeforeSelected={isBeforeSelected}
          isDesktop={isDesktop}
          isLoaded={isBeforeLoaded && isAfterLoaded && !isLoading}
          setIsBeforeSelected={setIsBeforeSelected}
          setIsLoaded={setIsAfterLoaded}
        >
          <Box
            bottom={0}
            display={{ base: 'none', tablet: 'flex' }}
            gap="200"
            justifyContent="center"
            pb={400}
            position="absolute"
            width="100%"
          >
            <Button
              color="light"
              iconAfter={iconEdit}
              label={t('embed.edit')}
              onClick={handleEditButtonClick}
              variant="glass"
            >
              {editButtonText}
            </Button>
            <Button
              color="light"
              iconAfter={iconInspiration}
              label={t('embed.moreIdeas')}
              onClick={handleMoreIdeasButtonClick}
              variant="glass"
            >
              {t('embed.moreIdeas')}
            </Button>
          </Box>
        </LandingPageImage>
      </HStack>
      <EmbedCustomizeMenu
        display={{ base: 'flex', tablet: 'none' }}
        isLoading={menuLoading}
        menu={menu}
        targetImage={targetImage}
        zIndex={1}
      />
    </Box>
  );
};
